import type { Router } from "vue-router";

// A navigation reloads, and so does a lazy part of the page on screen. A fetch
// nobody is waiting on (warm-up, icon kit) never does: it goes through
// loadInBackground and its failure waits for the next click.

const CHUNK_RELOAD_KEY = "knChunkReload";

// Browsers each word this differently, and none of them types it.
const CHUNK_FAILURE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

function isChunkFailure(error: unknown): boolean {
  return CHUNK_FAILURE.test(String((error as Error)?.message ?? ""));
}

// sessionStorage throws when site data is blocked; reloading beats throwing in an error handler.
function markedForReload(): boolean {
  try {
    return sessionStorage.getItem(CHUNK_RELOAD_KEY) !== null;
  } catch {
    return false;
  }
}

function setReloadMark(value: boolean): void {
  try {
    if (value) sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
    else sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch {
    // Nothing to do: see markedForReload.
  }
}

/** False if a reload was already tried with no successful navigation since. */
function reloadOnceForChunkFailure(path: string): boolean {
  if (markedForReload()) return false;
  setReloadMark(true);
  window.location.assign(path);
  return true;
}

// How many fetches nobody is waiting on are in flight. Vite reports a failure
// before the import rejects, so the count still holds the one that failed.
let backgroundLoads = 0;

/** Runs a chunk fetch whose failure must not reload the page someone is reading. */
export async function loadInBackground<T>(load: () => Promise<T>): Promise<T> {
  backgroundLoads++;
  try {
    return await load();
  } finally {
    backgroundLoads--;
  }
}

/** Vite reports a chunk's failed dependency (usually CSS) as `vite:preloadError` on `window`. */
export function installChunkReload(router: Router): void {
  let pending: string | null = null;
  // Set once this page has asked to be reloaded. A navigation can still finish
  // before the page unloads (a failed stylesheet lets its route's JS load), and
  // clearing the mark then would let the next page reload again, forever.
  let reloading = false;

  router.beforeEach((to) => {
    pending = to.fullPath;
  });

  router.onError((error, to) => {
    pending = null;
    if (!isChunkFailure(error)) return;
    // The navigation aborted, so the address bar still holds the old page.
    if (reloadOnceForChunkFailure(to.fullPath)) reloading = true;
  });

  window.addEventListener("vite:preloadError", (event) => {
    if (pending === null && backgroundLoads > 0) return;
    // Outside a navigation it is a lazy component of the page on screen, which
    // stays broken until something reloads it.
    const path = pending ?? window.location.pathname + window.location.search + window.location.hash;
    // preventDefault stops Vite rethrowing over the top of the reload.
    if (!reloadOnceForChunkFailure(path)) return;
    reloading = true;
    event.preventDefault();
  });

  router.afterEach(() => {
    pending = null;
    if (!reloading) setReloadMark(false);
  });
}
